import { ImagePlus } from "lucide-react";
import { Input } from "@project/components/ui/input";
import { Label } from "@project/components/ui/label";
import { Textarea } from "@project/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@project/components/ui/select";
import { Switch } from "@project/components/ui/switch";
import { PhotoUpload } from "@project/admin/components/photo-upload";
import { SearchableSelect } from "@project/admin/components/searchable-select";
import { donationMasterService } from "@project/services/admin/donation-master.service";
import {
  DONATION_SCHEME_AMOUNT_TYPE_OPTIONS,
  DONATION_SCHEME_MAX_AMOUNT,
  getDonationSchemeAmountError,
  type DonationSchemeAmountType,
} from "@project/types/donation-scheme";
import { cn } from "@project/lib/utils";

export type CampaignFormState = Record<string, unknown>;

export type CampaignFormFieldsProps = {
  editing: CampaignFormState | null;
  setEditing: (value: CampaignFormState | null) => void;
};

const amountTypeOptions = DONATION_SCHEME_AMOUNT_TYPE_OPTIONS.map(option => ({
  value: option.value,
  label: option.label,
}));

function patchEditing(
  editing: CampaignFormState | null,
  setEditing: CampaignFormFieldsProps["setEditing"],
  updates: CampaignFormState
) {
  setEditing({ ...(editing ?? {}), ...updates });
}

const MAX_AMOUNT_HELPER = "Maximum ₹1 Cr (1,00,00,000)";

function AmountNumberField({
  label,
  errorLabel,
  value,
  onChange,
  placeholder,
  required = false,
  helperText = MAX_AMOUNT_HELPER,
  error,
}: {
  label: string;
  errorLabel: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  helperText?: string;
  error?: string;
}) {
  const validationError =
    error ??
    getDonationSchemeAmountError(value, errorLabel, { required: false });
  const showError = Boolean(value !== "" && validationError);

  return (
    <div>
      <Label data-required={required ? "true" : undefined}>{label}</Label>
      <Input
        type="number"
        min={0}
        max={DONATION_SCHEME_MAX_AMOUNT}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        aria-invalid={showError}
        className={cn(
          "bg-muted/40 rounded-xl",
          showError
            ? "border-destructive focus-visible:ring-destructive"
            : "border-transparent"
        )}
      />
      {showError ? (
        <p className="mt-1 text-xs text-destructive">{validationError}</p>
      ) : (
        helperText && (
          <p className="mt-1 text-xs text-muted-foreground">{helperText}</p>
        )
      )}
    </div>
  );
}

function ToggleCard({
  label,
  description,
  checked,
  onCheckedChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}) {
  return (
    <div className="bg-muted/30 flex items-center justify-between gap-3 rounded-xl border border-border px-4 py-3">
      <div>
        <p className="text-sm font-medium text-foreground">{label}</p>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      <Switch checked={checked} onCheckedChange={onCheckedChange} />
    </div>
  );
}

export function CampaignFormFields({
  editing,
  setEditing,
}: CampaignFormFieldsProps) {
  const values = editing ?? {};
  const amountType = String(
    values.amount_type ?? "variable"
  ) as DonationSchemeAmountType;
  const mainCategoryId = values.main_category
    ? String(values.main_category)
    : "";

  const minContribution = String(values.min_contribution ?? "");
  const maxContribution = String(values.max_contribution ?? "");
  const minMaxError =
    minContribution !== "" &&
    maxContribution !== "" &&
    !getDonationSchemeAmountError(minContribution, "Minimum contribution") &&
    !getDonationSchemeAmountError(maxContribution, "Maximum contribution") &&
    Number(minContribution) > Number(maxContribution)
      ? "Minimum contribution cannot exceed maximum contribution."
      : undefined;

  const getContributionError = (
    value: string,
    errorLabel: string
  ): string | undefined => {
    if (value === "") {
      return undefined;
    }
    return (
      getDonationSchemeAmountError(value, errorLabel) ??
      minMaxError ??
      undefined
    );
  };

  return (
    <div className="space-y-4">
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="space-y-3">
          <div>
            <Label data-required="true">Title</Label>
            <Input
              value={String(values.title ?? "")}
              onChange={e =>
                patchEditing(editing, setEditing, { title: e.target.value })
              }
              placeholder="Build New Library"
              className="bg-muted/40 rounded-xl border-transparent"
            />
          </div>
          <div>
            <Label>Sub Title</Label>
            <Input
              value={String(values.sub_title ?? "")}
              onChange={e =>
                patchEditing(editing, setEditing, { sub_title: e.target.value })
              }
              placeholder="Help us expand learning space"
              className="bg-muted/40 rounded-xl border-transparent"
            />
          </div>
        </div>

        <div>
          <Label>Banner Image</Label>
          <div className="bg-muted/20 mt-1.5 flex min-h-[140px] items-center justify-center rounded-xl border border-dashed border-border p-4">
            <PhotoUpload
              value={
                values.banner_image ? String(values.banner_image) : undefined
              }
              onChange={url =>
                patchEditing(editing, setEditing, { banner_image: url })
              }
              folder="campaigns"
              size="md"
            />
          </div>
          {!values.banner_image && (
            <p className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
              <ImagePlus className="h-3.5 w-3.5" />
              Choose image...
            </p>
          )}
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <Label data-required="true">Donation Type</Label>
          <SearchableSelect
            value={String(values.donation_type ?? "")}
            onValueChange={(value, option) =>
              patchEditing(editing, setEditing, {
                donation_type: value,
                donation_type_name: option?.label ?? "",
              })
            }
            onSearch={query =>
              donationMasterService.listDonationTypesForLookup(query)
            }
            seedOption={
              values.donation_type && values.donation_type_name
                ? {
                    value: String(values.donation_type),
                    label: String(values.donation_type_name),
                  }
                : values.donation_type &&
                    typeof values.donation_type === "string" &&
                    !/^\d+$/.test(String(values.donation_type))
                  ? {
                      value: String(values.donation_type),
                      label: String(values.donation_type),
                    }
                  : undefined
            }
            placeholder="Select donation type"
            searchPlaceholder="Search donation types..."
            emptyText="No donation types found."
          />
        </div>
        <div>
          <Label data-required="true">Main Category</Label>
          <SearchableSelect
            value={mainCategoryId}
            onValueChange={(value, option) =>
              patchEditing(editing, setEditing, {
                main_category: value ? Number(value) : undefined,
                main_category_name: option?.label ?? "",
                sub_category: undefined,
                sub_category_name: "",
              })
            }
            onSearch={query =>
              donationMasterService.listMainCategoriesForLookup(query)
            }
            seedOption={
              values.main_category && values.main_category_name
                ? {
                    value: String(values.main_category),
                    label: String(values.main_category_name),
                  }
                : undefined
            }
            placeholder="Select main category"
            searchPlaceholder="Search main categories..."
            emptyText="No main categories found."
          />
        </div>
      </div>

      <div>
        <Label data-required="true">Sub Category</Label>
        <SearchableSelect
          value={String(values.sub_category ?? "")}
          onValueChange={(value, option) =>
            patchEditing(editing, setEditing, {
              sub_category: value ? Number(value) : undefined,
              sub_category_name: option?.label ?? "",
            })
          }
          onSearch={query =>
            donationMasterService.listSubCategoriesForLookup(
              mainCategoryId,
              query
            )
          }
          seedOption={
            values.sub_category && values.sub_category_name
              ? {
                  value: String(values.sub_category),
                  label: String(values.sub_category_name),
                }
              : undefined
          }
          placeholder={
            mainCategoryId
              ? "Select sub category"
              : "Select main category first"
          }
          searchPlaceholder="Search sub categories..."
          emptyText={
            mainCategoryId
              ? "No sub categories found."
              : "Select a main category first."
          }
          disabled={!mainCategoryId}
        />
      </div>

      <div>
        <Label>Description</Label>
        <Textarea
          value={String(values.description ?? "")}
          onChange={e =>
            patchEditing(editing, setEditing, { description: e.target.value })
          }
          placeholder="Fundraising for library construction"
          rows={3}
          className="bg-muted/40 rounded-xl border-transparent"
        />
      </div>

      <AmountNumberField
        label="Amount Needed"
        errorLabel="Amount needed"
        required
        value={String(values.amount_needed ?? "")}
        onChange={value =>
          patchEditing(editing, setEditing, { amount_needed: value })
        }
        placeholder="500000"
      />

      <div>
        <Label data-required="true">Amount Type</Label>
        <Select
          value={amountType}
          onValueChange={value =>
            patchEditing(editing, setEditing, {
              amount_type: value,
              fixed_amount: "",
              min_contribution: "",
              max_contribution: "",
            })
          }
        >
          <SelectTrigger className="bg-muted/40 rounded-xl border-transparent">
            <SelectValue placeholder="Select amount type" />
          </SelectTrigger>
          <SelectContent>
            {amountTypeOptions.map(option => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="mt-1 text-xs text-muted-foreground">
          {amountType === "fixed"
            ? "Donors contribute a single fixed amount."
            : "Donors can choose any amount within the min–max range."}
        </p>
      </div>

      {amountType === "fixed" ? (
        <AmountNumberField
          label="Fixed Amount (per contribution)"
          errorLabel="Fixed amount"
          required
          value={String(values.fixed_amount ?? "")}
          onChange={value =>
            patchEditing(editing, setEditing, { fixed_amount: value })
          }
          placeholder="1000"
        />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          <AmountNumberField
            label="Minimum Contribution (₹)"
            errorLabel="Minimum contribution"
            value={minContribution}
            onChange={value =>
              patchEditing(editing, setEditing, { min_contribution: value })
            }
            placeholder="100"
            error={getContributionError(
              minContribution,
              "Minimum contribution"
            )}
          />
          <AmountNumberField
            label="Maximum Contribution (₹)"
            errorLabel="Maximum contribution"
            value={maxContribution}
            onChange={value =>
              patchEditing(editing, setEditing, { max_contribution: value })
            }
            placeholder="100000"
            error={getContributionError(
              maxContribution,
              "Maximum contribution"
            )}
          />
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <Label data-required="true">Start Date</Label>
          <Input
            type="date"
            value={String(values.start_date ?? "").slice(0, 10)}
            onChange={e =>
              patchEditing(editing, setEditing, { start_date: e.target.value })
            }
            className="bg-muted/40 rounded-xl border-transparent"
          />
        </div>
        <div>
          <Label data-required="true">End Date</Label>
          <Input
            type="date"
            value={String(values.end_date ?? "").slice(0, 10)}
            onChange={e =>
              patchEditing(editing, setEditing, { end_date: e.target.value })
            }
            className="bg-muted/40 rounded-xl border-transparent"
          />
        </div>
      </div>

      <ToggleCard
        label="Active"
        description="Campaign is live"
        checked={values.is_active === true}
        onCheckedChange={checked =>
          patchEditing(editing, setEditing, { is_active: checked })
        }
      />

      <ToggleCard
        label="Allow donation after target"
        description="Continue accepting when goal is reached"
        checked={values.allow_donation_after_target === true}
        onCheckedChange={checked =>
          patchEditing(editing, setEditing, {
            allow_donation_after_target: checked,
          })
        }
      />
    </div>
  );
}
